import axios from "axios";

/**
 * RAG-32 — Lancement d'une synchronisation PIM manuelle depuis le back-office.
 *
 * Appelle l'API :
 *   - POST admins/sync/run           -> lance une synchro (périmètre all|pim|supplier|product)
 *   - GET  admins/sync/status/{id}   -> suit l'état d'une synchro en cours
 *
 * Le jeton admin est injecté automatiquement par l'intercepteur axios (auth.service).
 */

export type SyncScope = "all" | "pim" | "supplier" | "product";

export interface SyncRunResult {
  // 202 (synchro lourde mise en file) : identifiant du process à suivre.
  // 200 (produit resynchronisé) ou 204 (produit absent de son PIM) : pas de process.
  httpStatus: number;
  processId?: number;
}

export interface SyncError {
  status?: number;
  message: string;
}

function messageForStatus(error: any): string {
  const response = error?.response;
  const status = response?.status;
  // Message porté par l'API (throwErrorApi -> {error}, ou validation -> {message}).
  const apiMessage = response?.data?.error ?? response?.data?.message;

  switch (status) {
    case 403:
      return "Vous n'avez pas les droits pour lancer une synchronisation.";
    case 409:
      return "Une synchronisation de ce périmètre est déjà en cours.";
    case 404:
      return apiMessage || "Élément introuvable.";
    case 422:
      return apiMessage || "Demande invalide.";
    default:
      return "Veuillez contacter un administrateur";
  }
}

function run(scope: SyncScope, value?: string | number): Promise<SyncRunResult> {
  return new Promise<SyncRunResult>((resolve, reject) => {
    axios
      .post(`${process.env.REACT_APP_API_URL}admins/sync/run`, { scope, value })
      .then((response) => {
        resolve({
          httpStatus: response.status,
          processId: response.data?.process_id,
        });
      })
      .catch((error) => {
        reject({ status: error?.response?.status, message: messageForStatus(error) } as SyncError);
      });
  });
}

function status(processId: number): Promise<any> {
  return new Promise((resolve, reject) => {
    axios
      .get(`${process.env.REACT_APP_API_URL}admins/sync/status/${processId}`)
      .then((response) => resolve(response.data))
      .catch((error) => {
        reject({ status: error?.response?.status, message: messageForStatus(error) } as SyncError);
      });
  });
}

export default {
  run,
  status,
};
